import React, { useCallback } from 'react'
import Link from '@mui/material/Link'
import type { IDynamicFeedbackTargets } from '@common/interfaces/dynamicEnvFeedback'
import Text from '../../Common/Text'
import styles from './FeedbackPopover.module.scss'

interface IProps {
  isLoading: boolean
  error: string | null
  targets: IDynamicFeedbackTargets | null
  selectedTaskId: string | null
  onSelect: (taskId: string) => void
}

// Which prod ticket the feedback lands on: named when obvious, picked when it isn't.
const TargetPicker = ({ isLoading, error, targets, selectedTaskId, onSelect }: IProps) => {
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => onSelect(event.target.value),
    [onSelect],
  )

  if (isLoading) {
    return (
      <Text variant="bodySm" tone="secondary">
        Finding this PR&apos;s ticket…
      </Text>
    )
  }
  if (error || !targets) {
    return (
      <Text variant="bodySm" tone="error" casing="none">
        {error ?? "Couldn't find this PR's ticket."}
      </Text>
    )
  }
  if (!targets.targets.length) {
    return (
      <Text variant="bodySm" tone="error">
        No ACT ticket is linked to PR #{targets.pullRequestNumber}. Copy your feedback before
        closing this.
      </Text>
    )
  }

  const defaultTarget = targets.targets.find((target) => target.id === targets.defaultTargetId)
  if (defaultTarget) {
    return (
      <Text variant="bodySm" tone="secondary">
        Adds a subtask to{' '}
        <Link href={defaultTarget.url} target="_blank" rel="noopener noreferrer" underline="hover">
          {defaultTarget.title}
        </Link>
      </Text>
    )
  }

  return (
    <fieldset className={styles.targets}>
      <Text variant="bodySm" tone="secondary" component="legend">
        Which ticket is this feedback for?
      </Text>
      {targets.targets.map((target) => (
        <Text
          key={target.id}
          variant="bodySm"
          component="label"
          casing="none"
          className={styles.target}
        >
          <input
            type="radio"
            name="dynamic-feedback-target"
            value={target.id}
            checked={selectedTaskId === target.id}
            onChange={handleChange}
          />
          <span>{target.title}</span>
          {target.sectionName && (
            <Text variant="bodySm" component="span" tone="tertiary" casing="none">
              {target.sectionName}
            </Text>
          )}
        </Text>
      ))}
    </fieldset>
  )
}

export default TargetPicker
